import React, { createContext, useContext, useState, useEffect } from 'react';

// =====================================================
// CONTEXT: Gestione Carrello
// =====================================================

const CartContext = createContext();

const normalizeProductForCart = (product = {}) => {
  const normalizedId =
    product.id ??
    product.id_prodotto ??
    product.idProdotto ??
    product.productId ??
    null;

  const normalizedName =
    product.nome ??
    product.titolo ??
    product.name ??
    '';

  const normalizedPrice =
    product.prezzo ??
    product.price ??
    0;

  const normalizedStock = (() => {
    if (typeof product.quantita === 'number') return product.quantita;
    if (typeof product.stock === 'number') return product.stock;
    return null;
  })();

  return {
    ...product,
    id: normalizedId,
    nome: normalizedName,
    prezzo: normalizedPrice,
    stock: normalizedStock,
  };
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart deve essere usato dentro CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Carica carrello da localStorage
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('stibbertCart');
    if (!savedCart) {
      return [];
    }

    try {
      const parsed = JSON.parse(savedCart);
      return Array.isArray(parsed) ? parsed.map(normalizeProductForCart) : [];
    } catch {
      return [];
    }
  });

  // Salva carrello in localStorage quando cambia
  useEffect(() => {
    localStorage.setItem('stibbertCart', JSON.stringify(cart));
  }, [cart]);

  // Aggiungi prodotto al carrello
  const addToCart = (prodotto, quantita = 1) => {
    const normalizedProduct = normalizeProductForCart(prodotto);
    const maxStock = normalizedProduct.stock ?? Infinity;

    if (maxStock === 0) {
      console.warn('Prodotto esaurito, impossibile aggiungere al carrello.');
      return;
    }

    if (!normalizedProduct.id) {
      console.warn('Impossibile aggiungere al carrello: ID prodotto mancante', prodotto);
      return;
    }

    setCart(prevCart => {
      const esistente = prevCart.find(item => item.id === normalizedProduct.id);
      const safeQuantity = maxStock === Infinity ? quantita : Math.max(0, Math.min(quantita, maxStock));
      
      if (esistente) {
        // Aggiorna quantità
        return prevCart.map(item => {
          if (item.id !== normalizedProduct.id) return item;
          const itemStock = normalizedProduct.stock ?? item.stock ?? Infinity;
          const desired = item.quantita + safeQuantity;
          const clamped = itemStock === Infinity ? desired : Math.min(desired, itemStock);
          return {
            ...item,
            stock: itemStock === Infinity ? null : itemStock,
            quantita: clamped,
          };
        });
      } else {
        // Aggiungi nuovo prodotto
        const initialQuantity = maxStock === Infinity ? safeQuantity : Math.min(safeQuantity, maxStock);
        return [...prevCart, { ...normalizedProduct, quantita: initialQuantity }];
      }
    });
  };

  // Rimuovi prodotto dal carrello
  const removeFromCart = (prodottoId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== prodottoId));
  };

  // Aggiorna quantità prodotto
  const updateQuantity = (prodottoId, quantita) => {
    if (quantita <= 0) {
      removeFromCart(prodottoId);
      return;
    }

    setCart(prevCart =>
      prevCart.map(item =>
        item.id === prodottoId
          ? {
              ...item,
              quantita:
                item.stock === null || typeof item.stock === 'undefined'
                  ? quantita
                  : Math.min(quantita, item.stock),
            }
          : item
      )
    );
  };

  // Svuota carrello
  const clearCart = () => {
    setCart([]);
  };

  // Calcola subtotale prodotti
  const getSubtotal = () => {
    return cart.reduce((total, item) => {
      return total + (parseFloat(item.prezzo) * item.quantita);
    }, 0);
  };

  // Numero totale di articoli
  const getItemCount = () => {
    return cart.reduce((count, item) => count + item.quantita, 0);
  };

  const value = {
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotal: getSubtotal,
    getSubtotal,
    getItemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

